/**
 * What changed between two board snapshots — so an undo can point at it.
 *
 * The experience builder's history is a stack of whole-`tasks` snapshots rather than the task
 * builder's ops, and a snapshot does not say what it changed. So nothing was said: `history.undo()`
 * was called bare, a task moved or vanished or got rewired, and the board gave no sign which one.
 * That is the failure the task-builder contract names F1 — the data has moved either way, and a
 * teacher who cannot see what moved has been given a worse editor, not a safer one.
 *
 * Ops are not needed to fix it. The task builder needs them because it points at a FIELD; here the
 * unit is the task, and two arrays of tasks can simply be compared.
 */
import type { Task } from '@/types/experience';

export type TaskChange = 'added' | 'removed' | 'moved' | 'edited';

export interface BoardChange {
  kind: TaskChange;
  /** Task ids this step touched, in board order. Empty when the snapshots are equal. */
  ids: string[];
}

/**
 * Compare two snapshots, most specific kind first.
 *
 * Order matters: a step that adds a task also shifts the positions of everything after it, so
 * checking "moved" before "added" would describe every insertion as a move. Content changes are
 * compared last and only for tasks present in both.
 */
export function diffBoard(before: Task[], after: Task[]): BoardChange {
  const beforeIds = before.map((t) => t.id);
  const afterIds = after.map((t) => t.id);
  const beforeSet = new Set(beforeIds);
  const afterSet = new Set(afterIds);

  const added = afterIds.filter((id) => !beforeSet.has(id));
  if (added.length) return { kind: 'added', ids: added };

  const removed = beforeIds.filter((id) => !afterSet.has(id));
  if (removed.length) return { kind: 'removed', ids: removed };

  // Same membership: did anything change position?
  const moved = afterIds.filter((id, i) => beforeIds[i] !== id);
  if (moved.length) return { kind: 'moved', ids: moved };

  const byId = new Map(before.map((t) => [t.id, t]));
  const edited = after
    .filter((t) => JSON.stringify(byId.get(t.id)) !== JSON.stringify(t))
    .map((t) => t.id);
  return { kind: 'edited', ids: edited };
}

/**
 * Every changed task that is actually ON the board now, in board order.
 *
 * A step is not always one task. "Apply to Main Flow" stamps the scoring default across the whole
 * spine in a single undo entry, and undoing it moves every one of them back — so naming only the
 * first understates what just happened, and the teacher sees one card react to a change that
 * touched ten.
 *
 * Tasks no longer on the board are dropped rather than reported: an undo of an "add" removes the
 * task, and there is nothing to point at. That is the same call the task builder's contract makes
 * for redoing a delete (decision D-4, "say nothing") — a thing vanishing is self-evident in a way a
 * thing returning off-screen is not.
 *
 * Board order matters: the caller scrolls to the first of these, and the viewport can only go to
 * one place, so "first" has to mean topmost rather than whichever the diff happened to find first.
 */
export function tasksToPointAt(change: BoardChange, current: Task[]): string[] {
  const changed = new Set(change.ids);
  return current.map((t) => t.id).filter((id) => changed.has(id));
}
